import { useState } from "react";

import { useTimePreferences } from "@calcom/features/bookings/lib/timePreferences";
import { Dialog } from "@calcom/features/components/controlled-dialog";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { trpc } from "@calcom/trpc/react";
import { Button } from "@calcom/ui/components/button";
import { DialogContent, DialogFooter, DialogClose } from "@calcom/ui/components/dialog";
import { Input, Label } from "@calcom/ui/components/form";
import { showToast } from "@calcom/ui/components/toast";

type WaitlistRegistrationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventTypeId: number;
  eventTitle: string;
  /** Whether the `whatsapp-notifications` programme is live — gates the WhatsApp opt-in checkbox (US-11). */
  whatsAppEnabled?: boolean;
  /** Called after a successful registration (new OR already-registered) so the parent can hide the CTA. */
  onRegistered?: () => void;
};

/**
 * Public waitlist registration form (waitlist Phase 3). Collects the minimal PII (name, email, optional
 * phone) plus explicit RGPD consent, and posts to the rate-limited public endpoint. The desired window,
 * teamId, consentAt and retention horizon are all derived server-side from the event type.
 */
export const WaitlistRegistrationDialog = ({
  open,
  onOpenChange,
  eventTypeId,
  eventTitle,
  whatsAppEnabled = false,
  onRegistered,
}: WaitlistRegistrationDialogProps) => {
  const { t, i18n } = useLocale();
  const timezone = useTimePreferences((state) => state.timezone);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  // Explicit SMS opt-in (unchecked by default): the "slot available" SMS is only sent when the patient
  // ticks this box. Only meaningful with a phone — disabled (and cleared) while the phone is empty.
  const [smsOptIn, setSmsOptIn] = useState(false);
  // Explicit WhatsApp opt-in (US-11), independent of the SMS one and shown only when the programme is live.
  // Same phone requirement — cleared when the phone is emptied so a stale tick is never submitted.
  const [waOptIn, setWaOptIn] = useState(false);
  const [consent, setConsent] = useState(false);
  // Honeypot: hidden field real users never fill; bots that auto-fill forms reveal themselves.
  const [honeypot, setHoneypot] = useState("");

  const mutation = trpc.viewer.public.waitlistRegister.useMutation({
    onSuccess: (data) => {
      // The handler is idempotent: a repeat registration returns `alreadyRegistered` instead of a second
      // entry. Reflect that so the patient sees "you're already listed" rather than a false "just added".
      // `in` narrowing: `alreadyRegistered` only exists on the duplicate branch of the result union.
      const alreadyRegistered = "alreadyRegistered" in data && data.alreadyRegistered === true;
      showToast(
        alreadyRegistered ? t("waitlist_already_registered") : t("waitlist_registration_success"),
        "success"
      );
      onOpenChange(false);
      setName("");
      setEmail("");
      setPhone("");
      setSmsOptIn(false);
      setWaOptIn(false);
      setConsent(false);
      onRegistered?.();
    },
    onError: (error) => showToast(error.message || t("waitlist_registration_error"), "error"),
  });

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!consent) {
      showToast(t("waitlist_consent_required"), "error");
      return;
    }
    mutation.mutate({
      eventTypeId,
      attendeeName: name.trim(),
      attendeeEmail: email.trim(),
      attendeePhone: phone.trim() ? phone.trim() : undefined,
      smsOptIn: smsOptIn && Boolean(phone.trim()),
      waOptIn: whatsAppEnabled && waOptIn && Boolean(phone.trim()),
      timeZone: timezone,
      locale: i18n.language,
      consent: true,
      honeypot: honeypot || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* enableOverflow: le pied de dialog est `sticky bottom-0` avec un fond opaque ; sans conteneur
          scrollable, il recouvre la dernière ligne du long libellé de consentement (RGPD, 3 lignes). */}
      <DialogContent
        enableOverflow
        title={t("waitlist_dialog_title")}
        description={t("waitlist_dialog_description", { eventType: eventTitle })}>
        <form onSubmit={onSubmit} className="space-y-4">
          {/* Honeypot anti-bot. `autoComplete="off"` + les vrais champs ci-dessous porteant des hints
              `autoComplete` explicites : sans eux, Chrome remplissait ce champ caché (1er input texte)
              avec le nom enregistré de l'utilisateur → faux positif bloquant l'inscription. */}
          <input
            type="text"
            name="waitlist-hp"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={honeypot}
            onChange={(event) => setHoneypot(event.target.value)}
            style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
          />
          <div>
            <Label htmlFor="waitlist-name">{t("full_name")}</Label>
            <Input
              id="waitlist-name"
              name="name"
              autoComplete="name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="waitlist-email">{t("email_address")}</Label>
            <Input
              id="waitlist-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="waitlist-phone">{`${t("phone_number")} (${t("optional")})`}</Label>
            <Input
              id="waitlist-phone"
              name="tel"
              type="tel"
              autoComplete="tel"
              value={phone}
              onChange={(event) => {
                const value = event.target.value;
                setPhone(value);
                // No phone → the SMS/WhatsApp opt-ins are meaningless; clear them so a stale tick is never submitted.
                if (!value.trim()) {
                  setSmsOptIn(false);
                  setWaOptIn(false);
                }
              }}
            />
          </div>
          <label
            className={`flex items-start gap-2 text-sm ${!phone.trim() ? "cursor-not-allowed" : ""}`}
            htmlFor="waitlist-sms-opt-in">
            <input
              id="waitlist-sms-opt-in"
              type="checkbox"
              className="mt-1"
              checked={smsOptIn}
              disabled={!phone.trim()}
              onChange={(event) => setSmsOptIn(event.target.checked)}
            />
            <span className={phone.trim() ? "text-subtle" : "text-muted"}>
              {t("waitlist_sms_opt_in_label")}
            </span>
          </label>
          {whatsAppEnabled && (
            <label
              className={`flex items-start gap-2 text-sm ${!phone.trim() ? "cursor-not-allowed" : ""}`}
              htmlFor="waitlist-whatsapp-opt-in">
              <input
                id="waitlist-whatsapp-opt-in"
                type="checkbox"
                className="mt-1"
                checked={waOptIn}
                disabled={!phone.trim()}
                onChange={(event) => setWaOptIn(event.target.checked)}
              />
              <span className={phone.trim() ? "text-subtle" : "text-muted"}>
                {t("waitlist_whatsapp_opt_in_label")}
              </span>
            </label>
          )}
          <label className="text-default flex items-start gap-2 text-sm" htmlFor="waitlist-consent">
            <input
              id="waitlist-consent"
              type="checkbox"
              className="mt-1"
              checked={consent}
              onChange={(event) => setConsent(event.target.checked)}
            />
            <span className="text-subtle">{t("waitlist_consent_label")}</span>
          </label>
          <DialogFooter>
            <DialogClose />
            <Button type="submit" loading={mutation.isPending} disabled={!consent}>
              {t("waitlist_cta_button")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistRegistrationDialog;
